<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';

  import { fade } from 'svelte/transition';

  import { focusTrap, prepareFocus } from '@shared/libs/tools';
  import { IconButton } from '@shared/ui/buttons/icon-button';
  import { cn } from '@shared/utils';

  export let open: boolean = false;
  export let outsideClose: boolean = false;
  export let autoClose: boolean = false;
  export let dismissible: boolean = false;
  export let title: string = '';
  export let primaryButtonText: string = 'Yes';
  export let secondaryButtonText: string = 'Cancel';
  export let size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'lg';
  export let headerClasses = '';
  export let bodyClasses = '';
  export let footerClasses = '';
  export let hasFooter: boolean = true;

  let mounted = false;

  const dispatch = createEventDispatcher<{ close: Event; cancel: Event; confirm: Event }>();

  const onClose = (e: Event) => {
    if (dismissible) hide(e); // close on x button click
  };

  const onOutsideClose = (e: Event) => {
    if (outsideClose) hide(e); // close on click outside
  };

  const hide = (e: Event) => {
    e.preventDefault();
    open = false;
  };

  const onCancelClick = (e: Event) => {
    e.preventDefault();
    dispatch('cancel', e);
    if (autoClose) hide(e); // close on any button click
  };

  const onPrimaryClick = (e: Event) => {
    e.preventDefault();
    dispatch('confirm', e);
    if (autoClose) hide(e); // close on any button click
  };

  const handleKeys = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && dismissible) return hide(e);
  };

  const sizesClasses = {
    xs: 'xs:max-w-xs',
    sm: 'xs:max-w-sm',
    md: 'xs:max-w-md',
    lg: 'xs:max-w-lg',
    xl: 'xs:max-w-xl',
  };

  function isOpened(isOpen: boolean, mounted: boolean) {
    if (mounted) document.body.style.overflow = isOpen ? 'hidden' : '';
  }

  $: isOpened(open, mounted);

  onMount(() => {
    mounted = true;
  });
</script>

{#if open}
  <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
  <div
    class="font-neue xs:p-3 fixed inset-0 z-50 flex w-full items-center justify-center md:p-4"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    on:keydown="{handleKeys}"
    on:wheel|preventDefault|nonpassive
    use:prepareFocus
    use:focusTrap
  >
    <div
      on:click="{onOutsideClose}"
      class="dark:bg-default-900/75 fixed inset-0 bg-black/30 backdrop-blur-md"
      aria-hidden="true"
      in:fade="{{ duration: 200, delay: 0 }}"
      out:fade="{{ duration: 200, delay: 150 }}"
    ></div>

    <!-- orig ams properties left commented -->
    <!-- class="relative flex max-h-full w-full flex-col rounded-lg bg-default-200 shadow-md lg:max-w-lg dark:bg-dark-200" -->
    <div
      in:fade="{{ duration: 200, delay: 150 }}"
      out:fade="{{ duration: 200, delay: 0 }}"
      class="bg-default-50 relative flex max-h-full w-full flex-col rounded-lg shadow-md {sizesClasses[
        size
      ]} dark:bg-dark-200/80 dark:backdrop-blur-2xl"
    >
      <!-- class="shadow-md relative flex max-h-full w-full flex-col bg-default-50 {sizesClasses[size]} dark:bg-dark-200/80 dark:backdrop-blur-2xl" -->
      <!-- Header -->
      {#if $$slots.header || title}
        <slot name="header">
          <div
            class="{cn(
              'text-default-900 dark:text-dark-900 flex items-center justify-between gap-2 p-6 pb-2 md:p-8',
              headerClasses,
            )}"
          >
            <p class="text-xl font-bold">
              {title}
            </p>

            {#if dismissible}
              <IconButton size="{20}" icon="closeBig" on:click="{onClose}" variant="quaternary" />
            {/if}
          </div>
        </slot>
      {/if}
      <!-- Body -->
      <div
        on:keydown|stopPropagation="{handleKeys}"
        on:wheel|stopPropagation|passive
        role="document"
        class="{cn(
          'text-default-900 dark:text-dark-900 flex-1 overflow-y-auto overscroll-contain px-6 text-sm md:px-8',
          bodyClasses,
        )}"
      >
        <slot />
      </div>
      <!-- Footer -->
      {#if hasFooter}
        {#if $$slots.footer}
          <div class="{cn(footerClasses)}">
            <slot name="footer" />
          </div>
        {:else}
          <div class="{cn('flex items-center justify-start gap-2 p-6', footerClasses)}">
            <button hugged on:click="{onPrimaryClick}" classes="px-5">{primaryButtonText}</button>
            <button hugged on:click="{onCancelClick}" classes="px-5" variant="secondary">{secondaryButtonText}</button>
          </div>
        {/if}
      {:else}
        <div class="{cn('mb-8', footerClasses)}"></div>
      {/if}
    </div>
  </div>
{/if}
